import { Select } from "antd"
import { usePayrollStaffCategories } from "hooks/usePayrollStaffCategories"
import { PayrollSummaryReportSkeleton } from "../components/PayrollSkeletons"
import { useQuery } from "@tanstack/react-query"
import { ZIndex } from "constants/zIndex"
import { useLocation, useSearchParams } from "react-router-dom"
import { IGetReportSummary } from "../typings"
import { PAYROLL_ENDPOINTS, payrollService } from "services/payrollService"
import { DocumentArrowDownIcon } from "@heroicons/react/24/outline"
import { useAuthStore } from "stores"
import { formatCurrencyNoSymbol } from "utils/currency"
import { TextLink } from "components"
import { isPadeDomain } from "utils/utils"
import { mixPanelEvent } from "components/new-stuff/mix-panel/MixPanel"
import Papa from "papaparse"
import { getHireDateTime } from "./PayRollReportDetails"
import { AppBackButton } from "components/buttons/AppBackButton"
import EnhancedReportTable from "components/new-stuff/report-table/EnhancedReportTable"
import { downloadPayrollSummary } from "../history/downloadPayrollSummary"

const PayRollReportSummary = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const staffCategory = searchParams.get("staffCategory") || undefined
  const staffCategories = usePayrollStaffCategories()
  const staffCategoryControl = (
    <Select
      aria-label="Staff category"
      className="min-w-[180px]"
      getPopupContainer={(trigger) => trigger.parentElement || trigger}
      value={staffCategory || "All"}
      options={staffCategories.options ?? []}
      loading={staffCategories.isLoading}
      status={staffCategories.isError ? "error" : undefined}
      onChange={(value) => {
        searchParams.set("staffCategory", value)
        setSearchParams(searchParams)
      }}
    />
  )
  const id = searchParams.get("id")
  const { pathname } = useLocation()
  const { user } = useAuthStore()

  const isApproval = pathname.includes("approval")

  const { isLoading: isGetPayrollSummaryLoading, data: payrollSummary } =
    useQuery<IBaseRes<IGetReportSummary>>({
      queryKey: [
        PAYROLL_ENDPOINTS.VIEW_REPORT_SUMMARY,
        id,
        isApproval,
        staffCategory,
      ],
      queryFn: async () => {
        return await payrollService.viewReportSummary(
          id || "",
          isApproval,
          staffCategory
        )
      },
      refetchOnWindowFocus: false,
      cacheTime: 24 * 60 * 60 * 1000,
      onSuccess(data) {
        mixPanelEvent("payroll-report-summary-listing")
      },
    })

  // The report's own frame while it loads -- the back and download bar, the title -- with the
  // dates, address, period and tables in grey. This was a spinner alone on a white screen,
  // and the report then appeared as a bordered document out of nowhere.
  if (isGetPayrollSummaryLoading) {
    return (
      <div
        className="fixed inset-0 overflow-y-auto bg-white z-50"
        style={{
          zIndex: ZIndex.NotificationBar,
        }}
      >
        <div className="py-14 px-6 xl:px-16">
          <div className="border shadow-xl pb-6">
            <div className="flex justify-end p-6 mb-4 bg-neutral20 gap-3 items-center">
              <AppBackButton />
              {staffCategoryControl}
              <button
                className="px-4 py-2 font-avenir !text-sm text-a11y rounded-md bg-primary500 flex items-center opacity-50 cursor-not-allowed"
                disabled
              >
                <DocumentArrowDownIcon className="w-5 h-5 mr-1" />
                <span>Download</span>
              </button>
            </div>

            <div className="px-6">
              <h1 className="text-xl font-avenir text-neutral600 font-medium">
                Payroll summary report
              </h1>
              <hr className="my-4" />
            </div>

            <PayrollSummaryReportSkeleton />
          </div>
        </div>
      </div>
    )
  }

  const allowancesData =
    payrollSummary?.data?.payrollReportSummaryAllowances
      ?.filter((item) =>
        payrollSummary.data?.payrollReportSummaryEarnings?.some(
          (earning) =>
            earning.description === item.description &&
            hasMeaningfulValue(earning.amount)
        )
      )
      ?.map((item) => ({
        title: item.description,
        dataIndex: item.description,
        key: item.description,
        render: (text: any, record: any) => {
          const earning =
            payrollSummary.data?.payrollReportSummaryEarnings?.find(
              (earning) =>
                earning.person_id === record.person_id &&
                earning.description === item.description
            )

          const currency = payrollSummary?.data?.payrollReportSummaries?.find(
            (item) => item.person_id === record.person_id
          )?.currency_symbol

          return earning ? (
            <span className={"font-avenir text-neutral500 whitespace-nowrap"}>
              {currency} {formatCurrencyNoSymbol(Number(earning.amount || ""))}
            </span>
          ) : (
            ""
          )
        },
      }))
      .sort((a, b) => {
        if (a.title.toLowerCase() === "consolidated salary") return -1
        if (b.title.toLowerCase() === "consolidated salary") return 1
        return a.title.localeCompare(b.title)
      }) || []

  const trueBenefits = filterPropertiesWithTrue(
    payrollSummary && payrollSummary?.data?.payrollReportSummaryValues
      ? payrollSummary?.data?.payrollReportSummaryValues[0]
      : {}
  )

  const benefitsData: {
    title: string
    dataIndex: string
    key: string
    render: (text: any, record: any) => JSX.Element
  }[] = []

  const payrollSummaryValues = payrollSummary?.data?.payrollReportSummaryValues

  Object.keys(trueBenefits).forEach((key) => {
    const dataIndex = key.replace("has_", "")
    const title = generateTitle(dataIndex)

    payrollSummaryValues?.forEach((item) => {
      benefitsData.push({
        title,
        dataIndex,
        key: dataIndex,
        render: (text, record) => {
          return record[dataIndex] ? (
            <span className={"font-avenir text-neutral500 whitespace-nowrap"}>
              {record.currency_symbol}{" "}
              {formatCurrencyNoSymbol(Number(record[dataIndex] || ""))}
            </span>
          ) : (
            <></>
          )
        },
      })
    })
  })

  const hasSummaryValue = (key: string) =>
    payrollSummary?.data?.payrollReportSummaries?.some((row: any) =>
      hasMeaningfulValue(row?.[key])
    )

  const TableColumns: any[] = [
    hasSummaryValue("full_name") && {
      title: "Employee Name",
      dataIndex: "full_name",
      key: "full_name",
    },
    hasSummaryValue("identification_number") && {
      title: "Employee Number",
      dataIndex: "identification_number",
      key: "identification_number",
    },
    hasSummaryValue("employee_title") && {
      title: "Role",
      dataIndex: "employee_title",
      key: "employee_title",
    },
    hasSummaryValue("hire_date") && {
      title: "Hire Date",
      dataIndex: "hire_date",
      key: "hire_date",
    },
    hasSummaryValue("department") && {
      title: "Department",
      dataIndex: "department",
      key: "department",
    },
    hasSummaryValue("office_display_name") && {
      title: "Workplace",
      dataIndex: "office_display_name",
      key: "office_display_name",
    },
    hasSummaryValue("gender") && {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
    },
    hasSummaryValue("paygrade") && {
      title: "Paygrade",
      dataIndex: "paygrade",
      key: "paygrade",
    },
    hasSummaryValue("payband") && {
      title: "Payband",
      dataIndex: "payband",
      key: "payband",
    },
    ...allowancesData,
    {
      title: "Other Payables",
      dataIndex: "payable",
      key: "payable",
      render: (text: any, record: any) => {
        return (
          <span className={"font-avenir text-neutral500 whitespace-nowrap"}>
            {record.currency_symbol}{" "}
            {formatCurrencyNoSymbol(Number(record.payable || ""))}
          </span>
        )
      },
    },
    {
      title: "Gross Pay",
      dataIndex: "gross",
      key: "gross",
      render: (text: any, record: any) => {
        return (
          <span className={"font-avenir text-neutral500 whitespace-nowrap"}>
            {record.currency_symbol}{" "}
            {formatCurrencyNoSymbol(Number(record.gross || ""))}
          </span>
        )
      },
    },
    {
      title: "Tax",
      dataIndex: "tax",
      key: "tax",
      render: (text: any, record: any) => {
        return (
          <span className={"font-avenir text-neutral500 whitespace-nowrap"}>
            {record.currency_symbol}{" "}
            {formatCurrencyNoSymbol(Number(record.tax || ""))}
          </span>
        )
      },
    },
    ...benefitsData,
    {
      title: "Total Deductions",
      dataIndex: "deduction",
      key: "deduction",
      render: (text: any, record: any) => {
        return (
          <span className={"font-avenir text-neutral500 whitespace-nowrap"}>
            {record.currency_symbol}{" "}
            {formatCurrencyNoSymbol(Number(record.deduction || ""))}
          </span>
        )
      },
    },
    {
      title: "Net Pay",
      dataIndex: "net",
      key: "net",
      render: (text: any, record: any) => {
        return (
          <span className={"font-avenir text-neutral500 whitespace-nowrap"}>
            {record.currency_symbol}{" "}
            {formatCurrencyNoSymbol(Number(record.net || ""))}
          </span>
        )
      },
    },
    {
      title: "Days Worked",
      dataIndex: "days_worked",
      key: "days_worked",
      render: (text: any, record: any) => {
        return (
          <span className={"font-avenir text-neutral500 whitespace-nowrap"}>
            {record.days_worked}/{record.total_days}
          </span>
        )
      },
    },
  ].filter(Boolean)

  const TableData =
    payrollSummary?.data?.payrollReportSummaries
      ?.sort(
        (a, b) => getHireDateTime(a.hire_date) - getHireDateTime(b.hire_date)
      )
      ?.map((summary) => {
        const rowData: any = { ...summary }
        payrollSummary.data?.payrollReportSummaryAllowances?.forEach(
          (allowance) => {
            const earning =
              payrollSummary.data?.payrollReportSummaryEarnings?.find(
                (earning) =>
                  earning.person_id === summary.person_id &&
                  earning.description === allowance.description
              )
            rowData[allowance.description] = earning ? earning.amount : null
          }
        )
        return rowData
      }) || []

  const earningsTableData = []
  const getUniqueDescriptions = () => {
    const uniqueDescriptions: string[] = []
    if (payrollSummary?.data?.payrollReportSummaryEarnings?.length)
      for (
        let i = 0;
        i < payrollSummary?.data?.payrollReportSummaryEarnings?.length;
        i++
      ) {
        const description =
          payrollSummary.data?.payrollReportSummaryEarnings[i].description
        if (!uniqueDescriptions.includes(description)) {
          uniqueDescriptions.push(description)
        }
      }
    return uniqueDescriptions
  }
  if (getUniqueDescriptions().length) {
    for (let i = 0; i < getUniqueDescriptions().length; i++) {
      const desc = getUniqueDescriptions()[i]
      const foundItems =
        payrollSummary?.data?.payrollReportSummaryEarnings?.filter(
          (earning) => earning.description === desc
        )
      earningsTableData.push({
        description: desc,
        amount: (
          foundItems?.reduce((a: any, b: any) => a + b.amount, 0) as number
        )?.toLocaleString([], {
          minimumFractionDigits: 2,
        }),
        count: foundItems?.length,
      })
    }
  }

  const columns = [
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
    },
    {
      title: "Count",
      dataIndex: "count",
      key: "count",
    },
  ]

  return (
    <div
      className="fixed inset-0 overflow-y-auto bg-white z-50"
      style={{
        zIndex: ZIndex.NotificationBar,
      }}
    >
      <div className="py-14 px-6 xl:px-16">
        <div className="border shadow-xl">
          {/* ========Download Button======== */}
          {/* ========Download Button======== */}
          <div className="flex justify-end p-6 mb-4 bg-neutral20 gap-3 items-center">
            <AppBackButton />
            {staffCategoryControl}
            <button
              className="px-4 py-2 font-avenir !text-sm text-a11y rounded-md bg-primary500 flex items-center"
              onClick={() => id && downloadPayrollSummary(id, staffCategory)}
            >
              <DocumentArrowDownIcon className="w-5 h-5 mr-1" />
              <span>Download</span>
            </button>
          </div>

          {/* ========Table Header======== */}
          {/* ========Table Header======== */}
          <div className=" px-6">
            <h1 className="text-xl font-avenir text-neutral600 font-medium">
              Payroll summary report{" "}
              {payrollSummary?.data?.payrollStatusId &&
              payrollSummary?.data?.payrollStatusId < 4
                ? "(Draft)"
                : ""}
            </h1>
            <hr className="my-4" />

            <span className="text-neutral500 font-avenir ">
              {" "}
              Report created on {payrollSummary?.data?.report_date}{" "}
            </span>
          </div>
          <div className="flex flex-col items-end my-4 px-6 text-neutral500 text-sm font-avenir">
            <span> {user?.userOrganisation} </span>
            <span> {payrollSummary?.data?.officeAddress?.address_line_1} </span>
            <span>
              {" "}
              {payrollSummary?.data?.officeAddress?.address_city}{" "}
              {payrollSummary?.data?.officeAddress?.address_state}{" "}
              {payrollSummary?.data?.officeAddress?.zip_code}{" "}
              {payrollSummary?.data?.officeAddress?.country}{" "}
            </span>
          </div>
          <div className=" my-4 px-6 text-neutral500 text-sm font-avenir">
            <span className="font-semibold">
              {payrollSummary?.data?.qualifiedPayrollDate} Payroll.
            </span>{" "}
            Period: {payrollSummary?.data?.payPeriod}
          </div>
          <div className="px-6">
            {/* =======Table====== */}
            {/* =======Table====== */}
            <EnhancedReportTable
              loading={isGetPayrollSummaryLoading}
              dataSource={TableData}
              columns={TableColumns}
              rowKey={(record: any, index?: number) =>
                `payroll-summary-${
                  record?.person_id || record?.full_name || index
                }`
              }
              defaultFreezeBoundaryKey="full_name"
              pagination={false}
              scroll={{
                y: "calc(100vh - 300px)",
                x: "max-content",
              }}
              tableLayout="auto"
              bordered
              className="[&_thead_*]:!font-avenir [&_tbody_*]:!font-avenir [&_tbody_*]:!text-neutral500"
            />
          </div>
          <div className="px-6 my-4">
            {/* =======Earnings Summary Table====== */}
            {/* =======Earnings Summary Table====== */}
            <EnhancedReportTable
              loading={isGetPayrollSummaryLoading}
              dataSource={earningsTableData}
              columns={columns}
              rowKey={(record: any, index?: number) =>
                `earnings-summary-${record?.id || record?.description || index}`
              }
              defaultFreezeBoundaryKey="description"
              pagination={false}
              scroll={{
                y: "calc(100vh - 300px)",
                x: "max-content",
              }}
              tableLayout="auto"
              bordered
              className="[&_thead_*]:!font-avenir [&_tbody_*]:!font-avenir [&_tbody_*]:!text-neutral500"
            />
          </div>
        </div>
        {isPadeDomain() === true ? (
          <div className="mt-10 flex justify-center text-neutral500 text-sm font-avenir ">
            © {new Date().getFullYear()} PaidHR.{" "}
            <TextLink
              to="https://paidhr.com/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary500 text-sm !no-underline mx-1"
              title="Terms of Service"
            >
              Terms
            </TextLink>{" "}
            &{" "}
            <TextLink
              to="https://paidhr.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary500 text-sm !no-underline mx-1"
              title="Privacy Policy"
            >
              Privacy
            </TextLink>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default PayRollReportSummary

const generateTitle = (dataIndex: string) => {
  let title = dataIndex

  if (dataIndex.includes("deduction")) {
    title = title
      .replace("_deduction", " Employee")
      .replace("assurance", "Assurance")
      .replace("health_insurance", "NHIS")
  } else if (dataIndex.includes("contribution")) {
    title = title
      .replace("_contribution", " Employer")
      .replace("assurance", "Assurance")
      .replace("health_insurance", "NHIS")
  }

  // Capitalize the first letter of the title
  title = title.replace(/^\w/, (c) => c.toUpperCase())

  return title
    .replace(/_/g, " ")
    .replace("Benefit", "Benefits")
    ?.replace("hmo", "HMO")
    ?.replace("Other Employee", "Other Deductions")
    ?.replace("Housing Employee", "NHF Employee")
    ?.replace("Housing Employer", "NHF Employer")
}

function filterPropertiesWithTrue(obj: { [key: string]: any }): {
  [key: string]: boolean
} {
  return Object.keys(obj).reduce((acc, key) => {
    if (typeof obj[key] === "boolean" && obj[key] === true) {
      acc[key] = obj[key]
    }
    return acc
  }, {} as { [key: string]: boolean })
}

function hasMeaningfulValue(value: any) {
  if (value === null || value === undefined) return false
  if (typeof value !== "string") return true

  const normalized = value.trim().toUpperCase()
  return normalized !== "" && normalized !== "NA" && normalized !== "N/A"
}

// function downloadCSV(csv: any, filename: any) {
//   var csvFile
//   var downloadLink
//   // CSV file
//   csvFile = new Blob([csv], { type: "text/csv" })
//   //  Download link
//   downloadLink = document.createElement("a")
//   downloadLink.download = filename
//   downloadLink.href = window.URL.createObjectURL(csvFile)
//   downloadLink.style.display = "none"
//   document.body.appendChild(downloadLink)
//   downloadLink.click()
// }

// function exportTableToCSV(filename: any, header: string) {
//   var csv = []
//   // add the header row
//   csv.push(header)

//   // get all table rows
//   var rows = document.querySelectorAll("table tr")
//   // loop over each table row and populate array
//   for (var i = 0; i < rows.length; i++) {
//     var row = [],
//       //  get each column in the row
//       cols = rows[i].querySelectorAll("td, th")
//     for (var j = 0; j < cols.length; j++) {
//       // check if column is a div and if so , remove it
//       if (cols[j].querySelector("div")) {
//         //  remove div
//         cols[j].querySelector("div")?.remove()
//       } else if (cols[j].querySelector("span")) {
//         row.push(
//           cols[j]
//             ?.querySelector("span")
//             ?.innerHTML.replace(/,/g, "")
//             .replace(/₦/g, "")
//         )
//       } else row.push(cols[j].innerHTML.replace(/,/g, "").replace(/₦/g, "")) //  push column text into array
//     }
//     // join each row with a comma
//     csv.push(row.join(","))
//   }

//   downloadCSV(csv.join("\n"), filename)
// }
